import {
  type RefCallback,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import videojs from "video.js";
import { getDefaultVideoPlayerOptions } from "../videojsUtils";
import { attachReversePlayback } from "./attachReversePlayback";

export type VideoJsPlayer = ReturnType<typeof videojs>;

export interface UsePlayerOptions {
  src: string;
  type: string;
  initialVolume: number;
  initialMuted: boolean;
  initialPlaybackRate: number;
  disableInitialFocus?: boolean;
  onReady?: (player: VideoJsPlayer) => void;
  onError?: (e: unknown) => void;
  onVolumeChange?: (volume: number, muted: boolean) => void;
  onPlaybackRateChange?: (rate: number) => void;
}

export interface UsePlayerReturn {
  videoRef: RefCallback<HTMLDivElement>;
  videoElementNode: HTMLElement | null;
  playerRef: RefObject<VideoJsPlayer | null>;
  // Exposed for the hotkeys hook, which reads these synchronously
  // inside the keydown handler.
  playingRef: RefObject<boolean>;
  playbackRateRef: RefObject<number>;
  tempPlaybackRateRef: RefObject<number>;
  durationRef: RefObject<number | null>;
  // State
  playing: boolean;
  time: number;
  duration: number | null;
  muted: boolean;
  volume: number;
  playbackRate: number;
  tempPlaybackRate: number;
  bufferAmount: number;
  /** Intrinsic video height in pixels, or null until loadedmetadata fires. */
  videoHeight: number | null;
  // Actions
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  seek: (time: number) => void;
  setRate: (rate: number, persist: boolean) => void;
  setVolume: (volume: number) => void;
  setMuted: (muted: boolean) => void;
  setSrc: (src: string, type: string) => void;
}

/**
 * Owns the video.js player lifecycle: creation, event subscriptions, state,
 * and disposal. Returns refs for the React-managed wrapper div + the <video-js>
 * element (for portaling overlays), the player ref itself (for advanced
 * consumers like hotkeys), reactive state, and imperative actions.
 *
 * Reverse-playback support is included inline pending the decision on whether
 * it's load-bearing — if vestigial, the whole block can be deleted; if kept,
 * it can be lifted into a separate useReversePlayback hook.
 */
export function usePlayer(options: UsePlayerOptions): UsePlayerReturn {
  const {
    src,
    type,
    initialVolume,
    initialMuted,
    initialPlaybackRate,
    disableInitialFocus,
    onReady,
    onError,
    onVolumeChange,
    onPlaybackRateChange,
  } = options;

  const [videoNode, setVideoNode] = useState<HTMLDivElement | null>(null);
  const videoRef = useCallback<RefCallback<HTMLDivElement>>(
    (elt) => setVideoNode(elt),
    [],
  );
  const [videoElementNode, setVideoElementNode] = useState<HTMLElement | null>(
    null,
  );

  const playerRef = useRef<VideoJsPlayer | null>(null);

  // Reactive state
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState<number | null>(null);
  const [muted, setMutedState] = useState(initialMuted);
  const [volume, setVolumeState] = useState(initialVolume);
  const [playbackRate, setPlaybackRateState] = useState(initialPlaybackRate);
  const [tempPlaybackRate, setTempPlaybackRate] = useState(initialPlaybackRate);
  const [bufferAmount, setBufferAmount] = useState(0);
  /** Intrinsic video height in pixels, populated after loadedmetadata. */
  const [videoHeight, setVideoHeight] = useState<number | null>(null);

  // Refs that mirror state for synchronous reads inside event handlers
  // (ticker, reverse-playback wrappers, hotkeys consumer).
  const playingRef = useRef(false);
  const durationRef = useRef<number | null>(null);
  const playbackRateRef = useRef(initialPlaybackRate);
  const tempPlaybackRateRef = useRef(initialPlaybackRate);
  // Set when we change the player rate without wanting it persisted to settings
  // (e.g. JKL temp scrub speeds).
  const ephemeralRateChangeRef = useRef(false);
  const stateResettingRef = useRef(false);

  const commitDuration = (d: number) => {
    durationRef.current = d;
    setDuration(d);
  };

  // Imperative actions (defined before init effect so the effect's event
  // handlers can call them via stable closure). All have [] deps because they
  // only touch refs and React-stable setters.
  const play = useCallback(() => {
    playerRef.current?.play()?.catch?.(() => {});
  }, []);
  const pause = useCallback(() => {
    playerRef.current?.pause();
  }, []);
  const togglePlay = useCallback(() => {
    if (playerRef.current?.paused()) play();
    else pause();
  }, [play, pause]);
  const seek = useCallback((t: number) => {
    if (!playerRef.current) return;
    const clamped = Math.max(0, t);
    playerRef.current.currentTime(clamped);
    setTime(clamped);
  }, []);
  const setRate = useCallback((rate: number, persist: boolean) => {
    ephemeralRateChangeRef.current = !persist;
    playerRef.current?.playbackRate(rate);
    setTempPlaybackRate(rate);
    tempPlaybackRateRef.current = rate;
    if (persist) {
      setPlaybackRateState(rate);
      playbackRateRef.current = rate;
    }
  }, []);
  const setVolume = useCallback((v: number) => {
    const clamped = Math.max(0, Math.min(1, v));
    playerRef.current?.muted(false);
    playerRef.current?.volume(clamped);
  }, []);
  const setMuted = useCallback((m: boolean) => {
    playerRef.current?.muted(m);
  }, []);
  const setSrc = useCallback((newSrc: string, newType: string) => {
    if (!playerRef.current) return;
    const currentTime = playerRef.current.currentTime();
    const wasPaused = playerRef.current.paused();
    playerRef.current.src({ src: newSrc, type: newType });
    playerRef.current.one("loadedmetadata", () => {
      playerRef.current?.currentTime(currentTime);
      if (!wasPaused) playerRef.current?.play()?.catch?.(() => {});
    });
  }, []);

  // Initialize the player once a wrapper div is mounted.
  useEffect(() => {
    if (!videoNode || playerRef.current) return;

    const videoElement = document.createElement("video-js");
    videoElement.classList.add(
      "vjs-big-play-centered",
      "vjs-shade",
      "VIDEO",
      "relative",
    );
    setVideoElementNode(videoElement);
    videoNode.appendChild(videoElement);

    const playerOptions = {
      ...getDefaultVideoPlayerOptions(),
      sources: [{ src, type }],
    };

    const player = (playerRef.current = videojs(
      videoElement,
      playerOptions,
      () => onReady?.(player),
    ));

    videoElement.blur();
    const rawVideoElement = videoElement.querySelector("video");
    if (rawVideoElement) {
      rawVideoElement.style.pointerEvents = "none";
    }
    player.volume(initialVolume);
    player.muted(initialMuted);
    player.playbackRate(initialPlaybackRate);

    if (!disableInitialFocus) {
      player.focus();
    }

    // ---- Event subscriptions ----
    player.on("loadedmetadata", () => {
      stateResettingRef.current = false;
      const d = player.duration();
      if (Number.isFinite(d) && d > 0) commitDuration(d);
      setTime(Math.max(0, player.currentTime()));
      const innerVideo = player.el().querySelector("video") as
        | HTMLVideoElement
        | null;
      if (innerVideo?.videoHeight) setVideoHeight(innerVideo.videoHeight);
    });

    player.on("volumechange", () => {
      const v = player.volume();
      const m = player.muted();
      setMutedState(m);
      setVolumeState(m ? 0 : v);
      onVolumeChange?.(v, m);
    });

    player.on("ratechange", () => {
      const rate = player.playbackRate();
      if (typeof rate !== "number" || !isFinite(rate)) return;
      if (!ephemeralRateChangeRef.current) {
        setPlaybackRateState(rate);
        playbackRateRef.current = rate;
        onPlaybackRateChange?.(rate);
      }
      ephemeralRateChangeRef.current = false;
    });

    player.on("timeupdate", () => {
      if (stateResettingRef.current) return;
      const d = player.duration();
      if (Number.isFinite(d) && d > 0) {
        if (durationRef.current == null || Math.abs(d - durationRef.current) > 0.01) {
          commitDuration(d);
        }
      }
      setTime(Math.max(0, player.currentTime()));
    });

    player.on("progress", () => {
      const currentTime = player.currentTime();
      const buffered = player.buffered();
      for (let i = 0; i < buffered.length; ++i) {
        if (buffered.start(i) <= currentTime && buffered.end(i) >= currentTime) {
          setBufferAmount(buffered.end(i));
          return;
        }
      }
      setBufferAmount(0);
    });

    player.on("error", (e: unknown) => onError?.(e));

    // High-frequency time ticker — fires while playing for smoother scrubber
    // updates than the native timeupdate event (which only fires ~4 Hz).
    let animationFrameId = 0;
    const tick = () => {
      if (!playerRef.current) return;
      setTime(Math.max(0, playerRef.current.currentTime()));
      animationFrameId = requestAnimationFrame(tick);
    };

    // Closure-shared with attachReversePlayback so its wrappers can suppress
    // the next pause event when they synthetically pause the native player
    // (e.g. to transition into reverse mode without flickering playing=false).
    const suppressNextPauseRef = { current: false };

    player.on("play", () => {
      animationFrameId = requestAnimationFrame(tick);
      setPlaying(true);
      playingRef.current = true;
    });
    player.on("pause", () => {
      cancelAnimationFrame(animationFrameId);
      if (suppressNextPauseRef.current) {
        suppressNextPauseRef.current = false;
        return;
      }
      setPlaying(false);
      playingRef.current = false;
      // Returning to "natural" rate after a temp scrub speed ended.
      setRate(playbackRateRef.current, true);
    });

    const cleanupReverse = attachReversePlayback({
      player,
      playerRef,
      playingRef,
      playbackRateRef,
      tempPlaybackRateRef,
      initialPlaybackRate,
      setPlaying,
      setRate,
      suppressNextPauseRef,
    });

    return () => {
      cancelAnimationFrame(animationFrameId);
      cleanupReverse();
    };
  }, [videoNode]);

  // Dispose on unmount.
  useEffect(() => {
    return () => {
      if (playerRef.current && !playerRef.current.isDisposed()) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, []);

  return {
    videoRef,
    videoElementNode,
    playerRef,
    playingRef,
    playbackRateRef,
    tempPlaybackRateRef,
    durationRef,
    playing,
    time,
    duration,
    muted,
    volume,
    playbackRate,
    tempPlaybackRate,
    bufferAmount,
    videoHeight,
    play,
    pause,
    togglePlay,
    seek,
    setRate,
    setVolume,
    setMuted,
    setSrc,
  };
}
